import Vue from 'vue'
import * as Sentry from '@sentry/browser'
import { Integrations } from '@sentry/tracing'
import { Vue as VueIntegration } from '@sentry/integrations'
import { Breadcrumb, BreadcrumbHint } from '@sentry/types'
import { isProductionBuildType } from '@/environment-detection'

const formatBreadcrumb = (breadcrumb: Breadcrumb, hint: BreadcrumbHint) => {
  if (breadcrumb.category.startsWith('ui')) {
    const target: HTMLElement = hint.event.target
    const ancestorDataTestEls: HTMLElement[] = []

    // generate chain of all ancestor HTML elements with "data-test" attribute
    let element = target
    while (element) {
      if (element.dataset.test) ancestorDataTestEls.unshift(element)
      element = element.parentElement
    }

    const breadcrumbMessage = ancestorDataTestEls
      .map(
        element => `${element.tagName.toLowerCase()}[data-test='${element.dataset.test}']`
      )
      .join(' > ')

    if (breadcrumbMessage) {
      return {
        ...breadcrumb,
        data: {
          originalMessage: breadcrumb.message
        },
        message: breadcrumbMessage
      }
    }
  }

  return breadcrumb
}

/** Initializes the Sentry client in prod */
export function initSentry() {
  // We do not use sentry Vue integration because we are using our own custom Vue.config.errorHandler
  if (isProductionBuildType()) {
    console.log('Initializing sentry...') // eslint-disable-line no-console
    Sentry.init({
      dsn: process.env.VUE_APP_SENTRY_DSN,
      release: process.env.VUE_APP_VERSION,
      environment:
        process.env.VUE_APP_IS_PRODUCTION === 'true' ? 'production' : 'staging',
      beforeBreadcrumb: formatBreadcrumb,
      integrations: [
        new Integrations.BrowserTracing(),
        new VueIntegration({
          Vue,
          tracing: true,
          attachProps: true,
          tracingOptions: {
            trackComponents: true
          }
        })
      ]
    })
  }
}

/** Sends an error with additional infos to Sentry in prod */
export function sendErrorToSentry(error: Error, vm: Vue) {
  if (isProductionBuildType()) {
    Sentry.withScope(scope => {
      scope.setExtra(
        'propsData',
        vm.$options.propsData && JSON.parse(JSON.stringify(vm.$options.propsData))
      )
      scope.setExtra('componentName', vm.$options['_componentTag'])
      Sentry.captureException(error)
    })
  } else {
    // Log the unhandled exception to the console in non-production envs
    console.error(error.message, error) // eslint-disable-line no-console
  }
}
